/**
 * FAQ Categories Section
 * 
 * Handles:
 * - Filter tab switching (show/hide cards by category)
 * - Full-text search with dropdown results
 * - Modal open/close with FAQ accordion items
 * - Search result click → open modal + auto-expand accordion
 * 
 * Data structure:
 * - Categories (blocks) contain faq_item_group metaobjects
 * - Each faq_item_group contains faq_item metaobjects (question/answer pairs)
 */

class FaqCategoriesSection {
  constructor(section) {
    this.section = section;
    this.sectionId = section.dataset.sectionId;

    // Elements
    this.filterSlider = section.querySelector('[data-faq-filter-slider]');
    this.filterBar = section.querySelector('[data-faq-filter-bar]');
    this.filterPills = section.querySelectorAll('[data-filter]');
    this.searchInput = section.querySelector('[data-faq-search-input]');
    this.searchResults = section.querySelector('[data-faq-search-results]');
    this.searchResultsInner = section.querySelector('[data-faq-search-results-inner]');
    this.grid = section.querySelector('[data-faq-grid]');
    this.cards = section.querySelectorAll('.faq-categories__card');
    this.categoryGroups = section.querySelectorAll('.faq-categories__category-group');
    this.modal = section.querySelector('[data-faq-modal]');
    this.modalBackdrop = section.querySelector('[data-faq-modal-backdrop]');
    this.modalClose = section.querySelector('[data-faq-modal-close]');
    this.modalTitle = section.querySelector('[data-faq-modal-title]');
    this.modalContent = section.querySelector('[data-faq-modal-content]');
    this.modalFooter = section.querySelector('[data-faq-modal-footer]');

    // Splide instance for filter pills
    this.filterSplide = null;

    // State
    this.activeFilter = 'all';
    this.isModalOpen = false;
    this.previouslyFocusedElement = null;
    this.searchDebounceTimer = null;
    this.focusedResultIndex = -1;

    // Collect all FAQ items for search
    this.faqItems = this.collectFaqItems();

    // Bind methods
    this.handleFilterClick = this.handleFilterClick.bind(this);
    this.handleSearchInput = this.handleSearchInput.bind(this);
    this.handleSearchKeydown = this.handleSearchKeydown.bind(this);
    this.handleCardClick = this.handleCardClick.bind(this);
    this.handleModalClose = this.handleModalClose.bind(this);
    this.handleEscape = this.handleEscape.bind(this);
    this.handleClickOutsideSearch = this.handleClickOutsideSearch.bind(this);

    this.init();
  }

  init() {
    // Initialize filter pills slider for mobile
    this.initFilterSlider();

    // Filter pills
    this.filterPills.forEach(pill => {
      pill.addEventListener('click', this.handleFilterClick);
    });

    // Search
    if (this.searchInput) {
      this.searchInput.addEventListener('input', this.handleSearchInput);
      this.searchInput.addEventListener('keydown', this.handleSearchKeydown);
      this.searchInput.addEventListener('focus', () => {
        if (this.searchInput.value.trim().length >= 2) {
          this.showSearchResults();
        }
      });
    }

    // Card clicks
    this.cards.forEach(card => {
      const trigger = card.querySelector('[data-card-trigger]');
      if (trigger) {
        trigger.addEventListener('click', this.handleCardClick);
      }
    });

    // Modal close
    if (this.modalClose) {
      this.modalClose.addEventListener('click', this.handleModalClose);
    }
    if (this.modalBackdrop) {
      this.modalBackdrop.addEventListener('click', this.handleModalClose);
    }

    // Global listeners
    document.addEventListener('keydown', this.handleEscape);
    document.addEventListener('click', this.handleClickOutsideSearch);
  }

  /**
   * Initialize Splide slider for filter pills on mobile
   */
  initFilterSlider() {
    if (!this.filterSlider || typeof Splide === 'undefined') {
      return;
    }

    this.filterSplide = new Splide(this.filterSlider, {
      type: 'slide',
      perPage: 1,
      perMove: 1,
      autoWidth: true,
      focus: 0,
      omitEnd: true,
      pagination: false,
      arrows: false,
      drag: true,
      gap: '8px',
      mediaQuery: 'min',
      breakpoints: {
        768: {
          // Destroy slider on tablet/desktop
          destroy: true,
        }
      }
    });

    this.filterSplide.mount();

    // Remove tabindex from slides to prevent focus issues
    this.filterSplide.on('mounted', () => {
      const slides = this.filterSlider.querySelectorAll('.splide__slide');
      slides.forEach(slide => {
        slide.removeAttribute('tabindex');
      });
    });
  }

  /**
   * Collect all FAQ items for search indexing
   */
  collectFaqItems() {
    const items = [];
    this.cards.forEach(card => {
      const cardId = card.dataset.cardId;
      // Prefer the full title from the data attribute so search keeps markers
      // like "(New)" that are stripped from the visible card title.
      const cardTitle =
        card.dataset.cardTitle ||
        card.querySelector('.faq-categories__card-title')?.textContent.trim() ||
        '';
      
      // Add the category card itself as a searchable item
      if (cardTitle) {
        items.push({
          type: 'category',
          question: cardTitle.toLowerCase(),
          questionText: cardTitle,
          cardId: cardId,
          cardTitle: cardTitle
        });
      }
      
      const faqElements = card.querySelectorAll('[data-faq-question]');

      faqElements.forEach(el => {
        items.push({
          type: 'faq',
          question: el.dataset.faqQuestion,
          questionText: el.querySelector('.component-accordion-item__title strong')?.textContent || '',
          index: parseInt(el.dataset.faqIndex, 10),
          cardId: cardId,
          cardTitle: cardTitle
        });
      });
    });
    return items;
  }

  /**
   * Filter pill click handler
   */
  handleFilterClick(event) {
    const pill = event.currentTarget;
    const filter = pill.dataset.filter;

    if (filter === this.activeFilter) return;

    // Update active state
    this.filterPills.forEach(p => {
      p.classList.remove('faq-categories__filter-pill--active');
      p.setAttribute('aria-selected', 'false');
    });
    pill.classList.add('faq-categories__filter-pill--active');
    pill.setAttribute('aria-selected', 'true');

    // Scroll slider to active pill on mobile
    if (this.filterSplide && !this.filterSplide.state.is(Splide.STATES.DESTROYED)) {
      const slideIndex = Array.from(this.filterPills).indexOf(pill);
      if (slideIndex >= 0) {
        this.filterSplide.go(slideIndex);
      }
    }

    this.activeFilter = filter;
    this.filterCards(filter);
  }

  /**
   * Filter cards by category with sequential fade out → fade in
   */
  filterCards(filter) {
    const hideDelay = 250; // Match CSS transition duration
    const showDelay = 350; // Match CSS animation duration

    // Step 1: Hide all visible items first
    const visibleCards = Array.from(this.cards).filter(card => !card.hidden);
    const visibleGroups = Array.from(this.categoryGroups).filter(group => !group.hidden);

    visibleCards.forEach(card => {
      card.classList.add('faq-categories__card--hiding');
    });
    visibleGroups.forEach(group => {
      group.classList.add('faq-categories__category-group--hiding');
    });

    // Step 2: After fade out completes, hide everything and show filtered items
    setTimeout(() => {
      // Hide all cards and groups
      this.cards.forEach(card => {
        card.hidden = true;
        card.classList.remove('faq-categories__card--hiding');
      });
      this.categoryGroups.forEach(group => {
        group.hidden = true;
        group.classList.remove('faq-categories__category-group--hiding');
      });

      // Determine what to show based on filter
      const cardsToShow = [];
      const groupsToShow = [];

      if (filter === 'all') {
        // Show all
        cardsToShow.push(...this.cards);
        groupsToShow.push(...this.categoryGroups);
      } else {
        // Show only matching category
        this.cards.forEach(card => {
          if (card.dataset.categoryId === filter) {
            cardsToShow.push(card);
          }
        });
        this.categoryGroups.forEach(group => {
          if (group.dataset.categoryId === filter) {
            groupsToShow.push(group);
          }
        });
      }

      // Show filtered items with fade in animation
      cardsToShow.forEach(card => {
        card.hidden = false;
        requestAnimationFrame(() => {
          card.classList.add('faq-categories__card--showing');
        });
        setTimeout(() => {
          card.classList.remove('faq-categories__card--showing');
        }, showDelay);
      });
      groupsToShow.forEach(group => {
        group.hidden = false;
        requestAnimationFrame(() => {
          group.classList.add('faq-categories__category-group--showing');
        });
        setTimeout(() => {
          group.classList.remove('faq-categories__category-group--showing');
        }, showDelay);
      });
    }, hideDelay);
  }

  /**
   * Search input handler with debounce
   */
  handleSearchInput(event) {
    const query = event.target.value.trim().toLowerCase();

    clearTimeout(this.searchDebounceTimer);

    // Reset focused index when typing
    this.focusedResultIndex = -1;

    if (query.length < 2) {
      this.hideSearchResults();
      return;
    }

    this.searchDebounceTimer = setTimeout(() => {
      this.performSearch(query);
    }, 200);
  }

  /**
   * Handle keyboard navigation in search
   */
  handleSearchKeydown(event) {
    const results = this.searchResultsInner?.querySelectorAll('[data-search-result]');
    if (!results || results.length === 0) return;

    const isDropdownVisible = !this.searchResults?.hidden;
    if (!isDropdownVisible) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.focusedResultIndex = Math.min(this.focusedResultIndex + 1, results.length - 1);
        this.updateFocusedResult(results);
        break;

      case 'ArrowUp':
        event.preventDefault();
        this.focusedResultIndex = Math.max(this.focusedResultIndex - 1, -1);
        this.updateFocusedResult(results);
        // If going back to -1, keep focus in input
        if (this.focusedResultIndex === -1) {
          this.searchInput?.focus();
        }
        break;

      case 'Enter':
        event.preventDefault();
        if (this.focusedResultIndex >= 0 && results[this.focusedResultIndex]) {
          results[this.focusedResultIndex].click();
        }
        break;

      case 'Escape':
        event.preventDefault();
        this.hideSearchResults();
        this.focusedResultIndex = -1;
        break;

      case 'Tab':
        // Close dropdown on tab out
        this.hideSearchResults();
        this.focusedResultIndex = -1;
        break;
    }
  }

  /**
   * Update visual focus on search results
   */
  updateFocusedResult(results) {
    results.forEach((result, index) => {
      if (index === this.focusedResultIndex) {
        result.classList.add('faq-categories__search-result--focused');
        result.setAttribute('aria-selected', 'true');
        // Scroll into view with padding
        this.scrollResultIntoView(result);
      } else {
        result.classList.remove('faq-categories__search-result--focused');
        result.setAttribute('aria-selected', 'false');
      }
    });

    // Update aria-activedescendant on input
    if (this.focusedResultIndex >= 0 && results[this.focusedResultIndex]) {
      const resultId = results[this.focusedResultIndex].id || `search-result-${this.focusedResultIndex}`;
      results[this.focusedResultIndex].id = resultId;
      this.searchInput?.setAttribute('aria-activedescendant', resultId);
    } else {
      this.searchInput?.removeAttribute('aria-activedescendant');
    }
  }

  /**
   * Scroll a result into view with padding buffer
   */
  scrollResultIntoView(result) {
    if (!this.searchResults || !result) return;

    const container = this.searchResults;
    const scrollPadding = 8; // Padding buffer in pixels

    const containerRect = container.getBoundingClientRect();
    const resultRect = result.getBoundingClientRect();

    // Check if result is above visible area
    if (resultRect.top < containerRect.top + scrollPadding) {
      const scrollOffset = resultRect.top - containerRect.top - scrollPadding;
      container.scrollTop += scrollOffset;
    }
    // Check if result is below visible area
    else if (resultRect.bottom > containerRect.bottom - scrollPadding) {
      const scrollOffset = resultRect.bottom - containerRect.bottom + scrollPadding;
      container.scrollTop += scrollOffset;
    }
  }

  /**
   * Perform full-text search across all FAQ items
   */
  performSearch(query) {
    const results = this.faqItems.filter(item => {
      return item.question.includes(query) ||
        item.questionText.toLowerCase().includes(query);
    });

    // Sort results: categories first, then FAQ items
    results.sort((a, b) => {
      if (a.type === 'category' && b.type !== 'category') return -1;
      if (a.type !== 'category' && b.type === 'category') return 1;
      return 0;
    });

    this.renderSearchResults(results, query);
  }

  /**
   * Render search results dropdown
   */
  renderSearchResults(results, query) {
    if (!this.searchResultsInner) return;

    // Reset focused index
    this.focusedResultIndex = -1;

    if (results.length === 0) {
      this.searchResultsInner.innerHTML = `
        <p class="faq-categories__search-no-results">No results found for "${query}"</p>
      `;
    } else {
      const html = results.slice(0, 10).map((item, index) => {
        const isCategory = item.type === 'category';
        return `
          <button 
            type="button"
            class="faq-categories__search-result ${isCategory ? 'faq-categories__search-result--category' : ''}"
            id="search-result-${index}"
            role="option"
            aria-selected="false"
            data-search-result
            data-card-id="${item.cardId}"
            data-result-type="${item.type || 'faq'}"
            ${!isCategory ? `data-faq-index="${item.index}"` : ''}
          >
            <span class="faq-categories__search-result-text">${this.highlightMatch(item.questionText, query)}</span>
            ${!isCategory ? `<span class="faq-categories__search-result-category">${item.cardTitle}</span>` : ''}
          </button>
        `;
      }).join('');

      this.searchResultsInner.innerHTML = html;

      // Add click handlers to results
      this.searchResultsInner.querySelectorAll('[data-search-result]').forEach(result => {
        result.addEventListener('click', (e) => this.handleSearchResultClick(e));
      });
    }

    this.showSearchResults();
  }

  /**
   * Highlight matching text in search results
   */
  highlightMatch(text, query) {
    const regex = new RegExp(`(${this.escapeRegex(query)})`, 'gi');
    return text.replace(regex, '<strong>$1</strong>');
  }

  /**
   * Escape special regex characters
   */
  escapeRegex(string) {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  /**
   * Show search results dropdown
   */
  showSearchResults() {
    if (this.searchResults) {
      this.searchResults.hidden = false;
      this.searchResults.scrollTop = 0;
      this.searchInput?.setAttribute('aria-expanded', 'true');
    }
  }

  /**
   * Hide search results dropdown
   */
  hideSearchResults() {
    if (this.searchResults) {
      this.searchResults.hidden = true;
      this.searchInput?.setAttribute('aria-expanded', 'false');
      this.searchInput?.removeAttribute('aria-activedescendant');
      this.focusedResultIndex = -1;
    }
  }

  /**
   * Handle search result click
   */
  handleSearchResultClick(event) {
    event.preventDefault();
    event.stopPropagation();

    const result = event.currentTarget;
    const cardId = result.dataset.cardId;
    const resultType = result.dataset.resultType;
    const faqIndex = resultType === 'category' ? null : parseInt(result.dataset.faqIndex, 10);

    // Debug: log to verify data
    console.log('Search result clicked:', { cardId, resultType, faqIndex, result });

    if (!cardId) {
      console.error('No cardId found on search result');
      return;
    }

    // Clear search first
    if (this.searchInput) {
      this.searchInput.value = '';
    }
    this.hideSearchResults();

    // Open modal immediately
    this.openModal(cardId, faqIndex);
  }

  /**
   * Handle click outside search to close dropdown
   */
  handleClickOutsideSearch(event) {
    const searchWrapper = this.section.querySelector('.faq-categories__search-wrapper');
    // Don't close if clicking inside the search wrapper (includes results)
    if (searchWrapper && searchWrapper.contains(event.target)) {
      return;
    }
    this.hideSearchResults();
  }

  /**
   * Card click handler
   */
  handleCardClick(event) {
    const trigger = event.currentTarget;
    const cardId = trigger.dataset.cardTrigger;

    this.openModal(cardId);
  }

  /**
   * Open modal with FAQ items from a specific card
   */
  openModal(cardId, autoExpandIndex = null) {
    console.log('openModal called:', { cardId, autoExpandIndex });

    if (!this.modal) {
      console.error('Modal element not found');
      return;
    }

    // Use specific selector to find the card (not search results which also have data-card-id)
    const card = this.section.querySelector(`.faq-categories__card[data-card-id="${cardId}"]`);
    if (!card) {
      console.error('Card not found for cardId:', cardId);
      console.log('Available cards:', Array.from(this.cards).map(c => c.dataset.cardId));
      return;
    }

    // Find items container by class
    const itemsContainer = card.querySelector('.faq-categories__card-items');
    if (!itemsContainer) {
      console.error('Items container not found for cardId:', cardId);
      return;
    }

    // Get title and content
    const title = itemsContainer.querySelector('.faq-categories__modal-title')?.textContent || '';
    const contentSource = itemsContainer.querySelector('.faq-categories__card-items-list');

    console.log('Modal content found:', { title, contentSource, hasContent: !!contentSource?.innerHTML });

    // Populate modal
    if (this.modalTitle) {
      this.modalTitle.textContent = title;
    }
    if (this.modalContent && contentSource) {
      // Clone the content to preserve original
      this.modalContent.innerHTML = '';
      const clonedContent = contentSource.cloneNode(true);

      // Remove any existing accordion-initialized attributes so they can be re-initialized
      clonedContent.querySelectorAll('[data-accordion-initialized]').forEach(el => {
        el.removeAttribute('data-accordion-initialized');
      });

      // Move children into modal content
      while (clonedContent.firstChild) {
        this.modalContent.appendChild(clonedContent.firstChild);
      }

      // Initialize accordion items in modal after a frame
      requestAnimationFrame(() => {
        if (typeof initAccordionItems === 'function') {
          initAccordionItems(this.modalContent);
        }

        // Auto-expand specific FAQ item if index provided
        if (autoExpandIndex !== null) {
          const targetItem = this.modalContent.querySelector(`[data-faq-index="${autoExpandIndex}"] .component-accordion-item`);
          if (targetItem) {
            setTimeout(() => {
              targetItem.classList.add('is-open');
              const button = targetItem.querySelector('.component-accordion-item__title');
              if (button) {
                button.setAttribute('aria-expanded', 'true');
                button.focus();
              }
            }, 50);
          }
        }
      });
    }

    // Populate related topics in modal footer
    this.populateRelatedTopics(itemsContainer);

    console.log('Opening modal, setting hidden=false and adding is-active');

    // Save focus and open modal
    this.previouslyFocusedElement = document.activeElement;
    this.modal.hidden = false;

    // Force reflow for transition
    this.modal.offsetHeight;

    this.modal.classList.add('is-active');
    this.isModalOpen = true;

    console.log('Modal state:', { hidden: this.modal.hidden, classList: this.modal.classList.toString() });

    // Prevent body scroll
    document.body.style.overflow = 'hidden';

    // Focus close button
    this.modalClose?.focus();
  }

  /**
   * Populate related topics in modal footer
   */
  populateRelatedTopics(itemsContainer) {
    if (!this.modalFooter) return;

    const relatedTopics = itemsContainer.querySelector('[data-related-topics]');

    if (relatedTopics) {
      // Clone and populate footer
      this.modalFooter.innerHTML = '';
      const clonedRelated = relatedTopics.cloneNode(true);
      this.modalFooter.appendChild(clonedRelated);
      this.modalFooter.hidden = false;

      // Bind click events for related topic buttons
      const relatedButtons = this.modalFooter.querySelectorAll('[data-related-topic-trigger]');
      relatedButtons.forEach(button => {
        button.addEventListener('click', (e) => {
          const relatedCardId = e.currentTarget.dataset.relatedTopicTrigger;
          if (relatedCardId) {
            // Close current modal content and open new one
            this.openModal(relatedCardId);
            // Scroll modal content to top
            if (this.modalContent) {
              this.modalContent.scrollTop = 0;
            }
          }
        });
      });
    } else {
      // No related topics, hide footer
      this.modalFooter.hidden = true;
      this.modalFooter.innerHTML = '';
    }
  }

  /**
   * Close modal
   */
  handleModalClose() {
    if (!this.modal || !this.isModalOpen) return;

    this.modal.classList.remove('is-active');
    this.isModalOpen = false;

    // Wait for transition then hide
    setTimeout(() => {
      this.modal.hidden = true;
      if (this.modalContent) {
        this.modalContent.innerHTML = '';
      }
      if (this.modalFooter) {
        this.modalFooter.innerHTML = '';
        this.modalFooter.hidden = true;
      }
    }, 300);

    // Restore body scroll
    document.body.style.overflow = '';

    // Restore focus
    this.previouslyFocusedElement?.focus();
  }

  /**
   * Handle Escape key
   */
  handleEscape(event) {
    if (event.key === 'Escape') {
      if (this.isModalOpen) {
        this.handleModalClose();
      } else if (!this.searchResults?.hidden) {
        this.hideSearchResults();
      }
    }
  }

  /**
   * Cleanup
   */
  destroy() {
    document.removeEventListener('keydown', this.handleEscape);
    document.removeEventListener('click', this.handleClickOutsideSearch);

    // Destroy filter slider
    if (this.filterSplide) {
      this.filterSplide.destroy();
      this.filterSplide = null;
    }
  }
}

/**
 * Initialize FAQ Categories sections
 */
function initFaqCategories() {
  document.querySelectorAll('[data-section-type="faq-categories"]').forEach(section => {
    if (!section.faqCategoriesInstance) {
      section.faqCategoriesInstance = new FaqCategoriesSection(section);
    }
  });
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initFaqCategories);
} else {
  initFaqCategories();
}

// Shopify theme editor support
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', function (event) {
    const section = event.target.querySelector('[data-section-type="faq-categories"]');
    if (section) {
      if (section.faqCategoriesInstance) {
        section.faqCategoriesInstance.destroy();
      }
      section.faqCategoriesInstance = new FaqCategoriesSection(section);
    }
  });

  document.addEventListener('shopify:section:unload', function (event) {
    const section = event.target.querySelector('[data-section-type="faq-categories"]');
    if (section?.faqCategoriesInstance) {
      section.faqCategoriesInstance.destroy();
    }
  });
}
